import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getCmsArticles, createArticle, updateArticle, deleteArticle } from '../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, BookOpen, ExternalLink } from 'lucide-react';

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title_en: '', title_zh: '',
    summary_en: '', summary_zh: '',
    content_en: '', content_zh: '',
    image_url: '', slug: '', active: true
  });

  const fetchArticles = async () => {
    try {
      const res = await getCmsArticles();
      setArticles(res.data);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData(article);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingArticle(null);
    setFormData({
      title_en: '', title_zh: '',
      summary_en: '', summary_zh: '',
      content_en: '', content_zh: '',
      image_url: '', slug: '', active: true
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      // Generate slug if not provided
      const dataToSave = {
        ...formData,
        slug: formData.slug || formData.title_en.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      };

      if (editingArticle) {
        await updateArticle(editingArticle.id, dataToSave);
        toast.success('Article updated');
      } else {
        await createArticle(dataToSave);
        toast.success('Article created');
      }
      fetchArticles();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    try {
      await deleteArticle(id);
      setArticles(articles.filter(a => a.id !== id));
      toast.success('Article deleted');
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleToggleActive = async (article) => {
    try {
      await updateArticle(article.id, { active: !article.active });
      setArticles(articles.map(a => a.id === article.id ? { ...a, active: !a.active } : a));
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Blog Articles</h1>
          <p className="text-slate-600">Manage bilingual blog content</p>
        </div>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="w-4 h-4 mr-2" /> Add Article
        </Button>
      </div>

      <div className="space-y-4">
        {articles.map((article) => (
          <Card key={article.id} className={`p-4 bg-white border-2 border-slate-300 ${!article.active ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-4">
              {article.image_url && (
                <img src={article.image_url} alt="" className="w-20 h-20 object-cover rounded-lg" />
              )}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <BookOpen className="w-4 h-4 text-amber-500" />
                  <h3 className="font-semibold text-slate-900">{article.title_en}</h3>
                </div>
                <p className="text-sm text-slate-600 mb-1">{article.title_zh}</p>
                <p className="text-xs text-slate-400">/{article.slug}</p>
              </div>
              <div className="flex items-center gap-3">
                <a 
                  href={`/blog/${article.slug}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-amber-500"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
                <Switch checked={article.active} onCheckedChange={() => handleToggleActive(article)} />
                <Button variant="outline" size="icon" onClick={() => handleEdit(article)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" className="text-red-500" onClick={() => handleDelete(article.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Article Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? 'Edit Article' : 'Add New Article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title (English)</label>
                <Input value={formData.title_en} onChange={(e) => setFormData({ ...formData, title_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title (Chinese)</label>
                <Input value={formData.title_zh} onChange={(e) => setFormData({ ...formData, title_zh: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Summary (English)</label>
                <Textarea rows={2} value={formData.summary_en} onChange={(e) => setFormData({ ...formData, summary_en: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Summary (Chinese)</label>
                <Textarea rows={2} value={formData.summary_zh} onChange={(e) => setFormData({ ...formData, summary_zh: e.target.value })} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content (English)</label>
              <Textarea rows={8} value={formData.content_en} onChange={(e) => setFormData({ ...formData, content_en: e.target.value })} placeholder="Use **text** for headings, separate paragraphs with blank lines" />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Content (Chinese)</label>
              <Textarea rows={8} value={formData.content_zh} onChange={(e) => setFormData({ ...formData, content_zh: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">URL Slug</label>
                <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} placeholder="auto-generated-from-title" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={(checked) => setFormData({ ...formData, active: checked })} />
              <span className="text-sm">Published (visible on website)</span>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Save Article</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminArticles;
